import * as XLSX from 'xlsx';

export function durationToMinutes(durationString) {
    if (!durationString) return 0;
    const parts = String(durationString).split(':');
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    return hours * 60 + minutes;
}

export function durationToHours(durationString) {
    return durationToMinutes(durationString) / 60;
}

export function minutesToDurationLabel(total) {
    const minutes = Math.max(0, Math.round(total));
    return Math.floor(minutes / 60) + 'h' + String(minutes % 60).padStart(2, '0');
}

export function roundHours(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function formatConciergeName(concierge) {
    const base = `${concierge.lastName || ''} ${concierge.name || ''}`.trim() || concierge.email || concierge.id;
    return concierge.archive ? `${base} (archivé)` : base;
}

export function formatClientName(client) {
    return `${client.lastName || ''} ${client.name || ''}`.trim() || client.email || client.clientId || '';
}

export function getMonthsBetweenDates(start, end) {
    const months = [];
    const current = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

    while (current <= endMonth) {
        months.push({
            year: current.getFullYear(),
            month: current.getMonth() + 1,
            label: current.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
        });
        current.setMonth(current.getMonth() + 1);
    }

    return months;
}

export function monthInputToRange(monthValue) {
    if (!monthValue || !/^\d{4}-\d{2}$/.test(monthValue)) return null;
    const [yearStr, monthStr] = monthValue.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    if (!year || !month || month < 1 || month > 12) return null;

    const startDate = new Date(year, month - 1, 1, 0, 0, 0, 0);
    const endDate = new Date(year, month, 0, 23, 59, 59, 999);
    return { startDate, endDate, year, month };
}

export function isInterventionValidated(intervention) {
    if (!intervention.people || intervention.people.length === 0) return false;
    return intervention.people.every(person => person.report && person.finishDate);
}

export const HOURS_ANOMALY = {
    NOT_VALIDATED: 'not-validated',
    DEDUCED: 'deduced',
    MISSING: 'missing',
    OVERRUN: 'overrun'
};

export function computeInterventionPeopleHours(intervention, { conciergeIds } = {}) {
    const totalHours = durationToHours(intervention?.date?.duration);
    const people = (intervention?.people || [])
        .filter(person => (conciergeIds ? conciergeIds.has(person.people) : true));

    const result = {
        people,
        totalHours,
        declaredHours: 0,
        countedHours: 0,
        hoursByPerson: new Map(),
        anomalies: [],
        overrun: false
    };

    if (people.length === 0 || totalHours <= 0) return result;

    if (people.length === 1) {
        const person = people[0];
        if (person.finishDate) {
            result.hoursByPerson.set(person.people, totalHours);
        } else {
            result.hoursByPerson.set(person.people, 0);
            result.anomalies.push({
                conciergeId: person.people,
                kind: HOURS_ANOMALY.NOT_VALIDATED,
                reason: 'Intervention non validée : 0 h comptée'
            });
        }
        result.countedHours = sumMapValues(result.hoursByPerson);
        return result;
    }

    const declared = people.filter(person => person.workedDuration);
    const missing = people.filter(person => !person.workedDuration);

    declared.forEach(person => {
        result.hoursByPerson.set(person.people, durationToHours(person.workedDuration));
    });
    result.declaredHours = roundHours(sumMapValues(result.hoursByPerson));

    if (result.declaredHours > totalHours) {
        result.overrun = true;
        missing.forEach(person => result.hoursByPerson.set(person.people, 0));
        result.anomalies.push({
            conciergeId: null,
            kind: HOURS_ANOMALY.OVERRUN,
            reason: `Dépassement : ${result.declaredHours} h déclarées pour ${roundHours(totalHours)} h prévues`
        });
    } else if (missing.length === 1) {
        const deduced = roundHours(totalHours - result.declaredHours);
        result.hoursByPerson.set(missing[0].people, deduced);
        result.anomalies.push({
            conciergeId: missing[0].people,
            kind: HOURS_ANOMALY.DEDUCED,
            reason: `Heures déduites : ${deduced} h (${roundHours(totalHours)} h prévues moins ${result.declaredHours} h déclarées)`
        });
    } else if (missing.length > 1) {
        people.forEach(person => {
            result.hoursByPerson.set(person.people, 0);
            result.anomalies.push({
                conciergeId: person.people,
                kind: HOURS_ANOMALY.MISSING,
                reason: person.workedDuration
                    ? `Heures saisies (${person.workedDuration}) non comptées : ${missing.length} concierges n'ont pas saisi les leurs`
                    : `Heures non saisies, et ${missing.length} manquent sur l'intervention : 0 h comptée`
            });
        });
    }

    result.countedHours = roundHours(sumMapValues(result.hoursByPerson));
    return result;
}

function sumMapValues(map) {
    let total = 0;
    map.forEach(value => { total += value; });
    return total;
}

/**
 * Collects hours per property × concierge for a date range.
 * Includes archived concierges in the split so past hours stay correct.
 * Includes interventions on archived clients when present in the client list.
 */
export function collectPropertyConciergeHours({ clients = [], concierges = [], startDate, endDate }) {
    const conciergeById = new Map();
    concierges.forEach(c => {
        if (c?.id) conciergeById.set(c.id, c);
    });
    const conciergeIds = new Set(conciergeById.keys());

    // propertyKey -> { propertyId, propertyName, clientName, clientLastName, hoursByConcierge: Map }
    const propertyMap = new Map();
    // conciergeId -> display name (only those with hours)
    const activeConciergeIds = new Set();
    const anomalies = [];

    clients.forEach(client => {
        if (!Array.isArray(client.properties)) return;

        const clientName = formatClientName(client);
        const clientLastName = client.lastName || clientName;

        client.properties.forEach(property => {
            if (!Array.isArray(property.interventions)) return;

            const propertyId = property.id || property.name;
            const propertyName = property.name || propertyId || 'Propriété';
            const propertyKey = `${client.clientId || client.email || clientName}::${propertyId}`;

            property.interventions.forEach(intervention => {
                if (intervention.cancelled) return;

                const interventionDate = new Date(intervention.date?.dateStart);
                if (Number.isNaN(interventionDate.getTime())) return;
                if (interventionDate < startDate || interventionDate > endDate) return;

                const computed = computeInterventionPeopleHours(intervention, { conciergeIds });
                if (computed.people.length === 0 || computed.totalHours <= 0) return;

                computed.anomalies.forEach(anomaly => {
                    anomalies.push({
                        interventionId: intervention.id,
                        date: interventionDate,
                        dateLabel: interventionDate.toLocaleDateString('fr-FR'),
                        propertyName,
                        clientName,
                        conciergeName: anomaly.conciergeId
                            ? formatConciergeName(conciergeById.get(anomaly.conciergeId) || { id: anomaly.conciergeId })
                            : 'Ensemble des concierges',
                        kind: anomaly.kind,
                        reason: anomaly.reason
                    });
                });

                if (!propertyMap.has(propertyKey)) {
                    propertyMap.set(propertyKey, {
                        propertyKey,
                        propertyId,
                        propertyName,
                        clientName,
                        clientLastName,
                        hoursByConcierge: new Map()
                    });
                }

                const entry = propertyMap.get(propertyKey);
                computed.hoursByPerson.forEach((hours, cid) => {
                    if (hours <= 0) return;
                    activeConciergeIds.add(cid);
                    entry.hoursByConcierge.set(
                        cid,
                        (entry.hoursByConcierge.get(cid) || 0) + hours
                    );
                });
            });
        });
    });

    const conciergeColumns = Array.from(activeConciergeIds)
        .map(id => ({
            id,
            name: formatConciergeName(conciergeById.get(id) || { id })
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

    const properties = Array.from(propertyMap.values())
        .map(p => {
            let total = 0;
            p.hoursByConcierge.forEach(h => { total += h; });
            return { ...p, totalHours: roundHours(total) };
        })
        .filter(p => p.totalHours > 0)
        .sort((a, b) => {
            const byClient = (a.clientLastName || '').localeCompare(b.clientLastName || '', 'fr');
            if (byClient !== 0) return byClient;
            return (a.propertyName || '').localeCompare(b.propertyName || '', 'fr');
        });

    anomalies.sort((a, b) => (a.date - b.date) || a.propertyName.localeCompare(b.propertyName, 'fr'));

    return { properties, conciergeColumns, conciergeById, anomalies };
}

function appendAnomaliesRows(rows, anomalies) {
    if (!anomalies || anomalies.length === 0) return rows;
    rows.push([]);
    rows.push(['Anomalies']);
    rows.push(['Intervention', 'Date', 'Propriété', 'Client', 'Concierge', 'Motif']);
    anomalies.forEach(anomaly => {
        rows.push([
            anomaly.interventionId,
            anomaly.dateLabel,
            anomaly.propertyName,
            anomaly.clientName,
            anomaly.conciergeName,
            anomaly.reason
        ]);
    });
    return rows;
}

export function buildPropertyHoursAoa({ properties, conciergeColumns, anomalies }) {
    const headers = [
        'Propriété',
        'Client',
        ...conciergeColumns.map(c => c.name),
        'Total'
    ];
    const rows = [headers];

    properties.forEach(property => {
        const cells = conciergeColumns.map(col =>
            roundHours(property.hoursByConcierge.get(col.id) || 0)
        );
        rows.push([
            property.propertyName,
            property.clientName,
            ...cells,
            property.totalHours
        ]);
    });

    return appendAnomaliesRows(rows, anomalies);
}

export function buildConciergeHoursAoa({ properties, conciergeColumns, anomalies }) {
    const propertyColumns = properties.map(p => ({
        key: p.propertyKey,
        label: `${p.propertyName} (${p.clientLastName || p.clientName})`
    }));

    const headers = [
        'Concierge',
        ...propertyColumns.map(p => p.label),
        'Total'
    ];
    const rows = [headers];

    conciergeColumns.forEach(col => {
        let total = 0;
        const cells = propertyColumns.map(pc => {
            const property = properties.find(p => p.propertyKey === pc.key);
            const hours = roundHours(property?.hoursByConcierge.get(col.id) || 0);
            total += hours;
            return hours;
        });
        rows.push([
            col.name,
            ...cells,
            roundHours(total)
        ]);
    });

    return appendAnomaliesRows(rows, anomalies);
}

export function writeHoursWorkbook(aoa, sheetName, filename) {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    if (aoa.length > 0) {
        const maxWidths = aoa[0].map((_, colIndex) => {
            return Math.max(
                ...aoa.map(row => {
                    const cellValue = row[colIndex];
                    return cellValue != null ? String(cellValue).length : 10;
                })
            );
        });
        ws['!cols'] = maxWidths.map(width => ({ wch: Math.min(width + 2, 50) }));
    }

    const safeSheetName = String(sheetName || 'Heures').slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
    XLSX.writeFile(wb, filename);
}

export function exportPropertyConciergeMatrices({ clients, concierges, monthValue, mode }) {
    const range = monthInputToRange(monthValue);
    if (!range) {
        throw new Error('Mois invalide');
    }

    const { properties, conciergeColumns, anomalies } = collectPropertyConciergeHours({
        clients,
        concierges,
        startDate: range.startDate,
        endDate: range.endDate
    });

    if (properties.length === 0 && anomalies.length === 0) {
        return { empty: true, year: range.year, month: range.month };
    }

    const monthLabel = `${range.year}-${String(range.month).padStart(2, '0')}`;

    if (mode === 'property') {
        const aoa = buildPropertyHoursAoa({ properties, conciergeColumns, anomalies });
        writeHoursWorkbook(aoa, 'Par propriété', `heures_par_propriete_${monthLabel}.xlsx`);
        return {
            empty: false,
            mode: 'property',
            propertyCount: properties.length,
            conciergeCount: conciergeColumns.length,
            totalHours: roundHours(properties.reduce((sum, p) => sum + p.totalHours, 0)),
            anomalyCount: anomalies.length,
            year: range.year,
            month: range.month
        };
    }

    if (mode === 'concierge') {
        const aoa = buildConciergeHoursAoa({ properties, conciergeColumns, anomalies });
        writeHoursWorkbook(aoa, 'Par concierge', `heures_par_concierge_${monthLabel}.xlsx`);
        const totalHours = roundHours(
            conciergeColumns.reduce((sum, col) => {
                let cTotal = 0;
                properties.forEach(p => {
                    cTotal += p.hoursByConcierge.get(col.id) || 0;
                });
                return sum + cTotal;
            }, 0)
        );
        return {
            empty: false,
            mode: 'concierge',
            propertyCount: properties.length,
            conciergeCount: conciergeColumns.length,
            totalHours,
            anomalyCount: anomalies.length,
            year: range.year,
            month: range.month
        };
    }

    throw new Error('Mode d\'export inconnu');
}
